import { useQuery } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useLocation } from '@/shared/hooks/useLocation';
import { reviewKeys } from '../queryKeys';
import { captureAdminApi } from '../api/captureApi';
import { usePrivateFeedback } from '../hooks/usePrivateFeedback';
import type { PatientReviewItem } from '../api/captureApi';
import { PatientReviewCard } from './PatientReviewCard';
import styles from './CaptureTab.module.css';

export function SubmissionsSection() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  const { data: feedbackData, isLoading: feedbackLoading } = usePrivateFeedback();
  const { data: googleData, isLoading: googleLoading } = useQuery({
    queryKey: reviewKeys.captureGoogle(locationId),
    queryFn: () => captureAdminApi.listGoogleSubmissions(locationId),
    enabled: !!locationId,
    staleTime: 2 * 60 * 1000,
  });

  const isLoading = feedbackLoading || googleLoading;

  const reviews: PatientReviewItem[] = [
    ...(feedbackData?.data ?? []),
    ...(googleData?.data ?? []),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <Card padding="lg">
      <div className={patterns.panelTitle}>
        <MessageSquare size={16} aria-hidden /> Patient submissions
      </div>
      <p className={styles.captureDesc}>
        Submissions from patients who used your QR code, newest first. Google posts show up once the
        patient confirms the review went live — drafts they never published are not listed.
      </p>
      {isLoading ? (
        <div className={styles.feedbackEmpty}>Loading…</div>
      ) : reviews.length === 0 ? (
        <EmptyState
          icon={<MessageSquare size={24} aria-hidden />}
          title="No submissions yet"
          description="Private feedback and Google posts from your QR code will appear here."
        />
      ) : (
        <div className={styles.submissionList}>
          {reviews.map((review) => (
            <PatientReviewCard key={review.id} review={review} />
          ))}
        </div>
      )}
    </Card>
  );
}
